import React, { useState, useEffect } from 'react';
import { Settings, Zap, Mail, MessageSquare, Bot, Anchor, Home, CreditCard, BarChart3, Eye, EyeOff, Copy, TestTube, Save, RefreshCw, AlertTriangle, CheckCircle, Globe, Lock, Shield, Key, Link, Calendar, Users, FileText, Database, Cloud, Smartphone } from 'lucide-react';

const SettingsIntegrations = () => {
  const [activeCategory, setActiveCategory] = useState('communications');
  const [showApiKeys, setShowApiKeys] = useState({});
  const [outlookConfig, setOutlookConfig] = useState({
    clientId: '',
    clientSecret: '',
    tenantId: '',
    redirectUri: 'https://crm.priviacollection.com/auth/outlook/callback',
    scopes: [
      'https://graph.microsoft.com/Mail.ReadWrite',
      'https://graph.microsoft.com/Mail.Send',
      'https://graph.microsoft.com/Calendars.ReadWrite',
      'https://graph.microsoft.com/Contacts.ReadWrite',
      'https://graph.microsoft.com/User.Read'
    ],
    autoSync: true,
    syncInterval: 5,
    createContacts: true,
    linkReservations: true,
    emailSignature: true,
    readReceipts: true
  });

  // Catégories d'intégrations
  const integrationCategories = [
    { id: 'communications', label: 'Communications', icon: Mail, emoji: '📧' },
    { id: 'ai', label: 'Intelligence Artificielle', icon: Bot, emoji: '🤖' },
    { id: 'inventory', label: 'Inventaire', icon: Home, emoji: '🏠' },
    { id: 'payments', label: 'Paiements', icon: CreditCard, emoji: '💳' },
    { id: 'analytics', label: 'Analytics', icon: BarChart3, emoji: '📊' }
  ];

  // Données des intégrations
  const integrations = {
    communications: [
      {
        id: 'outlook',
        name: 'Microsoft Outlook (chris@priviacollection.com)',
        description: 'Intégration complète avec Outlook via Microsoft Graph API pour synchronisation bidirectionnelle des emails, calendrier et contacts.',
        status: 'disconnected',
        icon: '📮',
        priority: 'high',
        config: {
          email: 'chris@priviacollection.com',
          clientId: 'your-azure-app-client-id',
          tenantId: 'your-tenant-id',
          lastSync: null,
          totalEmails: 0,
          syncedContacts: 0,
          linkedReservations: 0
        },
        features: [
          'Envoi/réception d\'emails depuis le CRM',
          'Synchronisation automatique des contacts',
          'Liaison emails ↔ réservations',
          'Calendrier partagé avec l\'équipe',
          'Signatures automatiques PRIVIA',
          'Accusés de réception',
          'Archivage intelligent',
          'Recherche avancée dans les emails'
        ]
      },
      {
        id: 'sendgrid',
        name: 'SendGrid Email API',
        description: 'Service d\'envoi d\'emails transactionnels et marketing. Gérez vos campagnes et notifications automatiques.',
        status: 'connected',
        icon: '📧',
        config: {
          apiKey: 'SG.****************************',
          defaultEmail: 'noreply@priviacollection.com',
          senderName: 'PRIVIA Collection',
          templates: ['Confirmation réservation', 'Rappel pré-arrivée', 'Welcome email', 'Post-séjour']
        },
        usage: {
          thisMonth: 45287,
          limit: 100000,
          deliveryRate: 98.7
        }
      },
      {
        id: 'whatsapp',
        name: 'WhatsApp Business Custom',
        description: 'Votre outil WhatsApp personnalisé pour la communication instantanée avec les clients VIP.',
        status: 'connected',
        icon: '💬',
        config: {
          endpointUrl: 'https://wa.priviacollection.com/api',
          accessToken: 'EAAG****************************',
          phoneNumberId: '1234567890123456',
          webhookUrl: 'https://crm.priviacollection.com/webhook/whatsapp',
          verifyToken: 'privia_webhook_2025'
        },
        usage: {
          thisMonth: 2847,
          responseTime: '2.3 min',
          satisfaction: 96.2
        }
      }
    ],
    ai: [
      {
        id: 'openai',
        name: 'OpenAI GPT-4',
        description: 'Intelligence artificielle pour l\'assistance automatique, génération de contenu et analyse de sentiment.',
        status: 'connected',
        icon: '🧠',
        config: {
          apiKey: 'sk-****************************',
          model: 'gpt-4-turbo',
          maxTokens: 4000,
          temperature: 0.7
        }
      }
    ],
    inventory: [
      {
        id: 'pms',
        name: 'Property Management System',
        description: 'Système de gestion des propriétés pour synchroniser disponibilités et tarifs.',
        status: 'connected',
        icon: '🏠'
      }
    ],
    payments: [
      {
        id: 'stripe',
        name: 'Stripe Payments',
        description: 'Traitement des paiements sécurisé pour les réservations et services.',
        status: 'connected',
        icon: '💳'
      }
    ],
    analytics: [
      {
        id: 'analytics',
        name: 'Google Analytics 4',
        description: 'Suivi et analyse du comportement des utilisateurs sur vos plateformes.',
        status: 'connected',
        icon: '📊'
      }
    ]
  };

  const toggleApiKeyVisibility = (integrationId) => {
    setShowApiKeys(prev => ({
      ...prev,
      [integrationId]: !prev[integrationId]
    }));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const handleOutlookConnect = () => {
    // Simulation de la connexion OAuth avec Microsoft
    const authUrl = `https://login.microsoftonline.com/${outlookConfig.tenantId}/oauth2/v2.0/authorize?` +
      `client_id=${outlookConfig.clientId}&` +
      `response_type=code&` +
      `redirect_uri=${encodeURIComponent(outlookConfig.redirectUri)}&` +
      `scope=${encodeURIComponent(outlookConfig.scopes.join(' '))}&` +
      `response_mode=query`;
    
    console.log('Redirection vers:', authUrl);
    // window.open(authUrl, '_blank');
    alert('Redirection vers Microsoft OAuth (simulation)');
  };

  const renderOutlookIntegration = (integration) => (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="text-3xl">{integration.icon}</div>
          <div>
            <h3 className="text-lg font-semibold text-gray-800">{integration.name}</h3>
            <p className="text-sm text-gray-600 mt-1">{integration.description}</p>
          </div>
        </div>
        <div className={`px-3 py-1 rounded-full text-sm font-medium ${
          integration.status === 'connected' 
            ? 'bg-green-100 text-green-800' 
            : integration.status === 'error'
            ? 'bg-red-100 text-red-800'
            : 'bg-gray-100 text-gray-800'
        }`}>
          {integration.status === 'connected' ? 'Connecté' : 
           integration.status === 'error' ? 'Erreur' : 'Non connecté'}
        </div>
      </div>

      {integration.status === 'disconnected' && (
        <div className="space-y-6">
          {/* Configuration Azure App */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-3">
              <Shield className="w-5 h-5 text-blue-600" />
              <h4 className="font-semibold text-blue-800">Configuration Azure App</h4>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Client ID (Application ID)
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    value={outlookConfig.clientId}
                    onChange={(e) => setOutlookConfig({...outlookConfig, clientId: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tenant ID (Directory ID)
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    value={outlookConfig.tenantId}
                    onChange={(e) => setOutlookConfig({...outlookConfig, tenantId: e.target.value})}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Client Secret
                </label>
                <div className="relative">
                  <input
                    type={showApiKeys.outlookSecret ? 'text' : 'password'}
                    className="w-full px-3 py-2 pr-20 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Votre client secret Azure"
                    value={outlookConfig.clientSecret}
                    onChange={(e) => setOutlookConfig({...outlookConfig, clientSecret: e.target.value})}
                  />
                  <button
                    onClick={() => toggleApiKeyVisibility('outlookSecret')}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showApiKeys.outlookSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Redirect URI (à configurer dans Azure)
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                    value={outlookConfig.redirectUri}
                    readOnly
                  />
                  <button
                    onClick={() => copyToClipboard(outlookConfig.redirectUri)}
                    className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Permissions Microsoft Graph */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-3">
              <Key className="w-5 h-5 text-green-600" />
              <h4 className="font-semibold text-green-800">Permissions Microsoft Graph</h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {outlookConfig.scopes.map((scope, index) => (
                <div key={index} className="flex items-center space-x-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span className="text-green-700">{scope.split('/').pop()}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Options de synchronisation */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-3">
              <Settings className="w-5 h-5 text-yellow-600" />
              <h4 className="font-semibold text-yellow-800">Options de synchronisation</h4>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-gray-700">Synchronisation automatique</label>
                  <p className="text-xs text-gray-500">Synchroniser les emails toutes les 5 minutes</p>
                </div>
                <input
                  type="checkbox"
                  className="w-4 h-4 text-yellow-600 border-gray-300 rounded focus:ring-yellow-500"
                  checked={outlookConfig.autoSync}
                  onChange={(e) => setOutlookConfig({...outlookConfig, autoSync: e.target.checked})}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-gray-700">Créer contacts automatiquement</label>
                  <p className="text-xs text-gray-500">Ajouter nouveaux contacts à la base CRM</p>
                </div>
                <input
                  type="checkbox"
                  className="w-4 h-4 text-yellow-600 border-gray-300 rounded focus:ring-yellow-500"
                  checked={outlookConfig.createContacts}
                  onChange={(e) => setOutlookConfig({...outlookConfig, createContacts: e.target.checked})}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-gray-700">Lier emails aux réservations</label>
                  <p className="text-xs text-gray-500">Associer automatiquement emails et séjours</p>
                </div>
                <input
                  type="checkbox"
                  className="w-4 h-4 text-yellow-600 border-gray-300 rounded focus:ring-yellow-500"
                  checked={outlookConfig.linkReservations}
                  onChange={(e) => setOutlookConfig({...outlookConfig, linkReservations: e.target.checked})}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-gray-700">Signature PRIVIA automatique</label>
                  <p className="text-xs text-gray-500">Ajouter signature corporate aux emails</p>
                </div>
                <input
                  type="checkbox"
                  className="w-4 h-4 text-yellow-600 border-gray-300 rounded focus:ring-yellow-500"
                  checked={outlookConfig.emailSignature}
                  onChange={(e) => setOutlookConfig({...outlookConfig, emailSignature: e.target.checked})}
                />
              </div>
            </div>
          </div>

          {/* Fonctionnalités CRM */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-3">
              <Database className="w-5 h-5 text-purple-600" />
              <h4 className="font-semibold text-purple-800">Fonctionnalités CRM avancées</h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {integration.features.map((feature, index) => (
                <div key={index} className="flex items-center space-x-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-purple-600" />
                  <span className="text-purple-700">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Guide de configuration */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-3">
              <FileText className="w-5 h-5 text-gray-600" />
              <h4 className="font-semibold text-gray-800">Guide de configuration Azure</h4>
            </div>
            <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
              <li>Connectez-vous au <a href="https://portal.azure.com" target="_blank" className="text-blue-600 hover:underline">portail Azure</a></li>
              <li>Allez dans "Azure Active Directory" → "App registrations"</li>
              <li>Cliquez "New registration" et nommez votre app "PRIVIA CRM"</li>
              <li>Ajoutez l'URI de redirection : <code className="bg-gray-200 px-1 rounded">{outlookConfig.redirectUri}</code></li>
              <li>Dans "API permissions", ajoutez les permissions Microsoft Graph</li>
              <li>Générez un "Client secret" dans "Certificates & secrets"</li>
              <li>Copiez le Client ID, Tenant ID et Client Secret ci-dessus</li>
            </ol>
          </div>

          <div className="flex items-center justify-between pt-4">
            <div className="text-sm text-gray-600">
              <AlertTriangle className="w-4 h-4 inline mr-1" />
              Assurez-vous d'avoir configuré l'application Azure avant de connecter
            </div>
            <button
              onClick={handleOutlookConnect}
              disabled={!outlookConfig.clientId || !outlookConfig.tenantId || !outlookConfig.clientSecret}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              <Link className="w-4 h-4" />
              <span>Connecter Outlook</span>
            </button>
          </div>
        </div>
      )}

      {integration.status === 'connected' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <Mail className="w-5 h-5 text-green-600" />
                <div>
                  <div className="text-lg font-semibold text-green-800">{integration.config.totalEmails}</div>
                  <div className="text-sm text-green-600">Emails synchronisés</div>
                </div>
              </div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <Users className="w-5 h-5 text-blue-600" />
                <div>
                  <div className="text-lg font-semibold text-blue-800">{integration.config.syncedContacts}</div>
                  <div className="text-sm text-blue-600">Contacts synchronisés</div>
                </div>
              </div>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <Calendar className="w-5 h-5 text-purple-600" />
                <div>
                  <div className="text-lg font-semibold text-purple-800">{integration.config.linkedReservations}</div>
                  <div className="text-sm text-purple-600">Emails liés aux séjours</div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Dernière synchronisation : {integration.config.lastSync || 'Jamais'}
            </div>
            <div className="flex items-center space-x-2">
              <button className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center space-x-2">
                <RefreshCw className="w-4 h-4" />
                <span>Synchroniser</span>
              </button>
              <button className="px-4 py-2 text-red-600 border border-red-300 rounded-lg hover:bg-red-50">
                Déconnecter
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderIntegrationCard = (integration) => {
    if (integration.id === 'outlook') {
      return renderOutlookIntegration(integration);
    }

    return (
      <div key={integration.id} className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="text-2xl">{integration.icon}</div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800">{integration.name}</h3>
              <p className="text-sm text-gray-600 mt-1">{integration.description}</p>
            </div>
          </div>
          <div className={`px-3 py-1 rounded-full text-sm font-medium ${
            integration.status === 'connected' 
              ? 'bg-green-100 text-green-800' 
              : integration.status === 'error'
              ? 'bg-red-100 text-red-800'
              : 'bg-gray-100 text-gray-800'
          }`}>
            {integration.status === 'connected' ? 'Connecté' : 
             integration.status === 'error' ? 'Erreur' : 'Non connecté'}
          </div>
        </div>

        {integration.config && (
          <div className="space-y-3">
            {Object.entries(integration.config).map(([key, value]) => (
              <div key={key} className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700 capitalize">
                  {key.replace(/([A-Z])/g, ' $1').toLowerCase()}
                </span>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600">
                    {key.includes('key') || key.includes('token') || key.includes('secret') 
                      ? (showApiKeys[integration.id] ? value : '••••••••••••••••••••••••••••')
                      : Array.isArray(value) ? value.join(', ') : value
                    }
                  </span>
                  {(key.includes('key') || key.includes('token') || key.includes('secret')) && (
                    <button
                      onClick={() => toggleApiKeyVisibility(integration.id)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      {showApiKeys[integration.id] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {integration.usage && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Utilisation</h4>
            <div className="grid grid-cols-2 gap-4">
              {Object.entries(integration.usage).map(([key, value]) => (
                <div key={key} className="text-center">
                  <div className="text-lg font-semibold text-gray-800">{value}</div>
                  <div className="text-xs text-gray-600 capitalize">
                    {key.replace(/([A-Z])/g, ' $1').toLowerCase()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
          <button className="flex items-center space-x-2 text-sm text-gray-600 hover:text-gray-800">
            <TestTube className="w-4 h-4" />
            <span>Tester la connexion</span>
          </button>
          <div className="flex items-center space-x-2">
            <button className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
              Configurer
            </button>
            <button className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700">
              <Save className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Paramètres & Intégrations</h1>
            <p className="text-gray-600">Configurez vos intégrations et paramètres système</p>
          </div>
          <div className="flex items-center space-x-3">
            <button className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
              <RefreshCw className="w-4 h-4 mr-2 inline" />
              Actualiser
            </button>
            <button className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700">
              <Save className="w-4 h-4 mr-2 inline" />
              Sauvegarder
            </button>
          </div>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar des catégories */}
        <div className="w-64 bg-white border-r border-gray-200 min-h-screen">
          <div className="p-4">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
              Catégories
            </h3>
            <nav className="space-y-2">
              {integrationCategories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left transition-colors ${
                    activeCategory === category.id
                      ? 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <span className="text-lg">{category.emoji}</span>
                  <span className="font-medium">{category.label}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Contenu principal */}
        <div className="flex-1 p-6">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              {integrationCategories.find(cat => cat.id === activeCategory)?.label}
            </h2>
            <p className="text-gray-600">
              {activeCategory === 'communications' && 'Configurez vos outils de communication et messagerie'}
              {activeCategory === 'ai' && 'Intégrez des services d\'intelligence artificielle'}
              {activeCategory === 'inventory' && 'Synchronisez vos systèmes de gestion des propriétés'}
              {activeCategory === 'payments' && 'Configurez vos solutions de paiement'}
              {activeCategory === 'analytics' && 'Connectez vos outils d\'analyse et de reporting'}
            </p>
          </div>

          <div className="space-y-6">
            {integrations[activeCategory]?.map((integration) => (
              <div key={integration.id}>
                {renderIntegrationCard(integration)}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsIntegrations;